import _ from "underscore";
import DriveView from "../../DriveView";
import { NetworkStates, PageLevel } from "../../store/mobile/types";
import pageHTML from "./main.tmpl";
import logTemplate from "./main-log.tmpl";
import { handleApi } from "../../utils/error";
import { getServerDate } from "../../utils/storage/date";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import NavigateDateMenu from "../framework/navigation";
import { dstCalculator, getLocalDaySpan, getStartOfDayUserTimezone } from "../../utils/date";
import { populateAddressesRedux } from "../../sync/common";
import dayjs from "../../utils/dayjs";
import { hashToArray, hide, htmlEscape, isEqual, isVisible, show } from "../../utils/vanilla";
import Spinner, { BigSpinner } from "../../utils/spinner";
import { getHomeHash } from "../../utils/uri";
import { BaseCache, makeDeviceCache } from "../../utils/storage/cache";
import Trip from "../../model/Trip";
import { MobileActions } from "../../store/mobile/actions";
import mainScheduler from "../../utils/scheduler";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import GroupLinkedDevice from "../../model/GroupLinkedDevice";
import { getTripStartOfDay, TripActions } from "../../store/trips/actions";
import idSerializer from "geotab-uuid";
import Sync from "../../sync/sync";
import TripAnnotation from "../../model/TripAnnotation";
import { SyncType } from "../../sync/sync.types";
import { Device } from "../../model/Device";
import * as logger from "../../utils/logger";
import * as utils from "./utils";


const MAX_DAYS_SHOWN = 3;

interface IDailyTrips {
    trips: Trip[];
    businessDistance: number;
    personalDistance: number;
    uncategorizedDistance: number;
    hasUncategorizedTrips: boolean;
}

interface IDailyTripsDictionary {
    [dateHash: string]: IDailyTrips;
}

export default class TripsCategorizationView extends DriveView<Backbone.Model> {

    protected strings = [
        "Next day",
        "Previous day"
    ];
    private readonly template = _.template(pageHTML);
    private readonly logTemplate = _.template(logTemplate);
    private readonly navigationView: NavigateDateMenu | undefined;
    private readonly currentUser = this.app.userService.getCurrentUser();
    private readonly sync: Sync;
    private readonly deviceCache: BaseCache<Device | IDevice>;
    private readonly manageGroupDevice = this.app.securityService.can("manageGroupDevice");
    private readonly viewTripAnnotation = this.app.securityService.can("viewTripAnnotation");
    private readonly manageTripAnnotation = this.app.securityService.can("manageTripAnnotation");

    /**
     * Known IDs for the default activity groups. Used for filtering for this MVP but probably
     * needs to be removed in subsequent iterations.
     */
    private readonly defaultActivityGroups = ["GroupBusinessGroupId", "GroupPersonalGroupId"];

    private tripsDict: IDailyTripsDictionary = {};
    private reportDate: Date = getStartOfDayUserTimezone(getServerDate());
    private bigSpinner: BigSpinner | null = new BigSpinner();
    private reloadSpinner: Spinner | null = new Spinner();
    private debounceTimer: number;
    private needsSync = false;
    private isConnected: boolean;
    private cachedTrips?: IDailyTrips;

    constructor(options) {
        super(options, {
            title: "Trips",
            activeSection: "trips",
            pageLevel: document.getElementById("menu-trips") ? PageLevel.ONE : PageLevel.TWO
        });
        this.app = options.app;
        this.deviceCache = makeDeviceCache(this.app.store);
        this.isScrollBtnEnabled = true;
        this.sync = this.app.sync;
        this.isConnected = this.app.store.getState().mobile.online === NetworkStates.ONLINE;

        this.listenTo(dispatcher, DispatcherEvents.SyncTrips, (fromDate?: string) => {
            if (fromDate && fromDate === this.reportDate.toISOString()) {
                this.updateTrips(true);
            }
        });

        this.navigationView = new NavigateDateMenu({
            app: this.app,
            numDays: MAX_DAYS_SHOWN,
            callback: (newDate: Date) => {
                this.reportDate = newDate;
                this.cachedTrips = undefined;
                this.renderImpl();
            },
            unbounded: true
        });
    }

    logOrphanedAnnotations(dailyTripsByDevice: IIdDictionary<Trip[]>, tripAnnotationsInStore: IIdDictionary<IIdDictionary<TripAnnotation>>, fromDate: number, toDate: number) {
        // For each annotation belong to the current day, check if it is associated with a trip.
        for (const deviceId in tripAnnotationsInStore) {
            const dailyTrips = dailyTripsByDevice[deviceId] || {};
            if (dailyTrips.length > 0) {
                const tripAnnotationsForDevice = tripAnnotationsInStore[deviceId] || {};
                for (const annotationId in tripAnnotationsForDevice) {
                    const annotation = tripAnnotationsForDevice[annotationId];
                    if (this.isOrphan(annotation, dailyTrips, fromDate, toDate)) {
                        logger.log(logger.Source.UI, `Orphan trip annotation found ${ annotationId } ${ annotation.dateTime }`);
                    }
                }
            }
        }
    }

    isOrphan(annotation: TripAnnotation, dailyTrips: Trip[], fromDate: number, toDate: number) {
        const annotationDateTime = annotation.dateTime && new Date(annotation.dateTime).getTime();
        if (annotationDateTime && annotationDateTime >= fromDate && annotationDateTime <= toDate) {
            const orphanFound = !dailyTrips.some(trip => trip._annotation?.id === annotation.id);
            if (orphanFound) {
                return true;
            }
        }
        return false;
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click .trip-annotation-cancel": this.cancel,
            "click .trip-annotation-save": this.save,
            "change .categorize-trip-input": this.selectTripType,
            "click .editAnnotation": this.editAnnotation,
            "click #reloadTrips": this.renderImpl,
            "click #reloadTripsOffline": this.reloadTripsOffline
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render() {
        if (!this.app.securityService.can("showTrips")) {
            this.app.router.navigate(getHomeHash(), true);
            return this;
        }

        this.app.localizerService.initUserUnits(this.translator)
            .then(() => this.renderImpl())
            .catch(handleApi);

        return this;
    }

    remove() {
        super.remove();
        this.clearTripCategorizationSyncInterval();
        this.clearDebounceTimeout();
        this.bigSpinner?.stop();
        this.bigSpinner = null;
        this.reloadSpinner?.stop();
        this.reloadSpinner = null;
        this.navigationView && this.navigationView.remove();
        this.needsSync && this.app.sync.synchronize(false, undefined, [SyncType.TripAnnotation, SyncType.GroupLinkedDevice]);
        return this;
    }

    private toggleRefreshSpinner(start: boolean) {
        utils.toggleSpinner(this.reloadSpinner, start, "reload-spinner", "reloadTripsOffline", ".reload-spinner-container");
    }

    private toggleLoading(start: boolean) {
        utils.toggleSpinner(this.bigSpinner, start, "trips-loading", "trips-display", ".spinner-container");
    }

    private renderUncategorizedTrips(): Promise {
        let reportDateString = this.reportDate.toISOString();
        let localDaySpan = getLocalDaySpan(this.reportDate);
        let dailyTrips = this.tripsDict.hasOwnProperty(reportDateString) && this.tripsDict[reportDateString].trips || [];
        if (!dailyTrips.length) {
            return Promise.resolve();
        }

        let getLocalDate = (tripDate: string) => dayjs(dstCalculator.toLocalDateTime(tripDate)).format("HH:mm");
        let dailyAddresses = utils.tripListToIAddressSearchList(dailyTrips);
        return populateAddressesRedux(this.app.api, this.app.store, dailyAddresses, true)
            .then(() => {
                let tripTemplate = "";
                for (let i = 0; i < dailyTrips.length; i++) {
                    let trip = dailyTrips[i];
                    let device = this.deviceCache.get({ id: trip.device.id });

                    let tripFromAddress = "";
                    let tripFromCity = "";
                    let tripToAddress = "";
                    let tripToCity = "";

                    let tripStartPreviousDay = i === 0 && (trip.start && new Date(trip.start) < localDaySpan.fromDate);
                    let tripEndNextDay = i === dailyTrips.length - 1 && (trip.stop && new Date(trip.stop) > localDaySpan.toDate);

                    if (trip._startPoint) {
                        let fromAddress = utils.getAddressFromRedux(this.app.store, trip._startPoint);
                        if (fromAddress) {
                            let [fromStreet, ...fromCity] = utils.splitAddress(fromAddress.formattedAddress);
                            tripFromAddress = fromStreet;
                            tripFromCity = (fromCity || []).join(",");
                        }
                    }

                    if (trip.stopPoint) {
                        let toAddress = utils.getAddressFromRedux(this.app.store, trip.stopPoint);
                        if (toAddress) {
                            let [toStreet, ...toCity] = utils.splitAddress(toAddress.formattedAddress);
                            tripToAddress = toStreet;
                            tripToCity = (toCity || []).join(",");
                        }
                    }

                    tripTemplate += this.logTemplate({
                        id: trip.id,
                        tripStartPreviousDay,
                        tripFromTime: tripStartPreviousDay ? this.getString("Previous day") : getLocalDate(trip.start || getServerDate().toISOString()),
                        tripFromAddress,
                        tripFromCity,
                        tripEndNextDay,
                        tripToTime: tripEndNextDay ? this.getString("Next day") : getLocalDate(trip.stop || getServerDate().toISOString()),
                        tripDistance: tripEndNextDay ? "" : utils.convertToLocalDistanceString(this.app.localizerService, trip.distance),
                        tripToAddress,
                        tripToCity,
                        annotation: trip._annotation ? htmlEscape(trip._annotation.text) : "",
                        device: device && device[0],
                        isBusinessTripTypeChecked: trip._tripType && trip._tripType === "Business" ? "checked" : undefined,
                        isPersonalTripTypeChecked: trip._tripType && trip._tripType === "Personal" ? "checked" : undefined,
                        groupLinkedDeviceId: trip._groupLinkedDeviceId,
                        manageGroupDevice: this.manageGroupDevice,
                        viewTripAnnotation: this.viewTripAnnotation,
                        manageTripAnnotation: this.manageTripAnnotation
                    });
                }

                let container = this.el.querySelector("#uncategorized-trips-container");
                if (container) {
                    return this.display(tripTemplate, undefined, container);
                }
                return Promise.resolve();
            })
            .catch(handleApi);
    }

    private getNewTripDay(): IDailyTrips {
        return {
            trips: [],
            hasUncategorizedTrips: false,
            businessDistance: 0,
            personalDistance: 0,
            uncategorizedDistance: 0
        };
    }

    private compileDailyTrips(tripDay: IDailyTrips, trip: Trip, groupLinkedDevices: IIdDictionary<GroupLinkedDevice>, tripAnnotations: IIdDictionary<TripAnnotation>) {
        this.categorizeTrip(trip, groupLinkedDevices);
        this.getAnnotationForTrip(trip, tripAnnotations);
        tripDay.trips.push(trip);

        this.updateTripDailySummary(tripDay, trip);
    }

    private categorizeTrip(trip: Trip, groupLinkedDevices: IIdDictionary<GroupLinkedDevice>) {
        let groupLinkedDevicesForCurrentTrip: GroupLinkedDevice[] = [];
        logger.log(logger.Source.UI, `Categorizing Trip: ${ JSON.stringify(trip) } GroupLinkedDevices: ${ JSON.stringify(groupLinkedDevices) }`);
        for (let gid in groupLinkedDevices) {
            if (this.defaultActivityGroups.indexOf(groupLinkedDevices[gid].group.id) !== -1) {
                let groupLinkedDevice = groupLinkedDevices[gid];
                if (trip._midPoint && trip._midPoint === groupLinkedDevice.to && trip._midPoint === groupLinkedDevice.from) {
                    groupLinkedDevicesForCurrentTrip.push(groupLinkedDevice);
                } else if (trip._midPoint && trip._midPoint <= groupLinkedDevice.to && trip._midPoint > groupLinkedDevice.from) {
                    groupLinkedDevicesForCurrentTrip.push(groupLinkedDevice);
                } else if (trip.stop && trip.stop <= groupLinkedDevice.to && trip.stop > groupLinkedDevice.from) {
                    groupLinkedDevicesForCurrentTrip.push(groupLinkedDevice);
                } else if (typeof trip.stop === "undefined" && trip.start && trip.start >= groupLinkedDevice.from) {
                    groupLinkedDevicesForCurrentTrip.push(groupLinkedDevice);
                }
            }
        }
        if (!groupLinkedDevicesForCurrentTrip.length) {
            logger.log(logger.Source.UI, `Trip can not be categorized id: ${ trip.id }`);
        }

        let uniqueGroupDeviceLinks = Array.from(new Set(groupLinkedDevicesForCurrentTrip));
        let sortedGroupDeviceLinks = uniqueGroupDeviceLinks.sort((a, b) => (!a.from || !b.from) ? 0 : (a.from > b.from ? 1 : -1));
        if (sortedGroupDeviceLinks.length > 0) {
            logger.log(logger.Source.UI, `Categorizing trip: ${ trip.id } with GroupDevice entity: ${ sortedGroupDeviceLinks[0].id }`);
            let earliestGroup = sortedGroupDeviceLinks[0];
            trip._tripType = earliestGroup.group.name;
            trip._groupLinkedDeviceId = earliestGroup.id;
            if (this.currentUser) {
                let currentUserTrips = this.app.store.getState().trips[this.currentUser.id];
                let userTrips = { ...currentUserTrips };
                userTrips[trip.id] = trip;
                this.app.store.dispatch(TripActions.setTrips({ userId: this.currentUser.id, trips: userTrips, startOfDay: getTripStartOfDay(this.currentUser.timeZoneId) }));
            }
        }
    }

    private getAnnotationForTrip(trip: Trip, tripAnnotations: IIdDictionary<TripAnnotation>) {
        for (let annotationId in tripAnnotations) {
            let tripAnnotationDate = tripAnnotations[annotationId].dateTime;
            const tripEnd = trip.stop;
            if (trip._midPoint) {
                if (tripAnnotationDate && trip.start && tripEnd && tripAnnotationDate >= trip.start && tripAnnotationDate < tripEnd) {
                    trip._annotation = new TripAnnotation(tripAnnotations[annotationId]);
                    break;
                }
            } else if (trip.start && tripAnnotationDate === trip.start) {
                trip._annotation = new TripAnnotation(tripAnnotations[annotationId]);
                break;
            }
        }
    }

    private updateTrips(fromSync: boolean) {
        let trips = this.app.store.getState().trips;
        if (!trips || !this.currentUser) {
            return this.displayTrips(fromSync);
        }

        let userTrips = hashToArray(trips[this.currentUser.id] || {});
        if (userTrips.length === 0) {
            return this.displayTrips(fromSync);
        }

        let groupLinkedDevicesInStore = this.app.store.getState().groupLinkedDevices;
        let tripAnnotationsInStore = this.app.store.getState().tripAnnotations;

        let localDateRange = getLocalDaySpan(this.reportDate);
        let fromDate = localDateRange.fromDate.getTime();
        let toDate = localDateRange.toDate.getTime();
        let sortedTrips = userTrips
                            .filter(trip => {
                                let start = trip.start && new Date(trip.start).getTime();
                                let stop = new Date(trip.stop || getServerDate().getTime()).getTime();
                                return start && ((start > fromDate && start < toDate) || (start < fromDate && stop > fromDate));
                            })
                            .sort((a, b) => (!a.start || !b.start) ? 0 : (a.start > b.start ? 1 : -1));

        let dateHash = localDateRange.fromDate.toISOString();
        let newTripDay = this.getNewTripDay();

        const dailyTripsByDevice: IIdDictionary<Trip[]> = {};
        sortedTrips.forEach(trip => {
            let tripDeviceId = trip.device.id;
            let groupLinkedDevices = groupLinkedDevicesInStore[tripDeviceId] || {};
            let tripAnnotations = tripAnnotationsInStore[tripDeviceId] || {};
            const newTrip = new Trip(trip);
            this.compileDailyTrips(newTripDay, newTrip, groupLinkedDevices, tripAnnotations);
            if (!dailyTripsByDevice[tripDeviceId]) {
                dailyTripsByDevice[tripDeviceId] = [];
            }
            dailyTripsByDevice[tripDeviceId].push(newTrip);
        });

        this.logOrphanedAnnotations(dailyTripsByDevice, tripAnnotationsInStore, fromDate, toDate);

        let isOnline = this.app.store.getState().mobile.online === NetworkStates.ONLINE;
        let existingDay = this.tripsDict[dateHash];

        this.toggleRefreshSpinner(false);
        if (!fromSync || this.isConnected !== isOnline || !existingDay || existingDay.trips.length === 0 || !isEqual(this.cachedTrips || existingDay, newTripDay)) {
            this.tripsDict[dateHash] = newTripDay;
            this.cachedTrips = undefined;
            return this.displayTrips(fromSync);
        }

        return Promise.resolve();
    }

    private getTripFromId(id: string) {
        let dailyTrips = this.tripsDict[this.reportDate.toISOString()]?.trips || [];
        return dailyTrips.filter(trip => trip.id === id)[0];
    }

    private cancel(e) {
        let id = e.currentTarget?.getAttribute("data-id") as string;
        if (id) {
            this.toggleErrorMessage(id, false);

            let tripCommentContainer = document.querySelector(`#${ id } .trip-comment-container`) as HTMLElement;
            if (tripCommentContainer) {
                let tripCommentField = tripCommentContainer.querySelector("input");
                if (tripCommentField) {
                    tripCommentField.value = "";
                }
                hide(tripCommentContainer);

                let trip = this.getTripFromId(id);
                if (trip && trip._annotation) {
                    let tripAnnotationContainer = document.querySelector(`#${ id } .trip-annotation-container`) as HTMLElement;
                    if (tripAnnotationContainer) {
                        let tripAnnotationField = tripAnnotationContainer.querySelector(".trip-annotation");
                        if (tripAnnotationField) {
                            tripAnnotationField.textContent = trip._annotation.text;
                        }
                        show(tripAnnotationContainer);
                    }
                }
            }

            let trip = this.app.store.getState().trips[(this.currentUser as IUser).id][id];
            if (trip._tripType) {
                let selectorId = "categorize-trip__" + trip._tripType.toLowerCase() + "_" + trip.id;
                let selectedRadioButton = document.querySelector(`#${ selectorId }`) as HTMLInputElement;
                if (selectedRadioButton) {
                    selectedRadioButton.checked = true;
                }
            } else {
                let selectedRadioButton = document.querySelector(`#${ id } .categorize-trip-input:checked`) as HTMLInputElement;
                if (selectedRadioButton) {
                    selectedRadioButton.checked = false;
                }
            }
        }
    }

    private addEditAnnotation(trip?: Trip, tripAnnotation?: string) {
        if (!tripAnnotation) {
            return false;
        }

        if (trip) {
            let existingAnnotation = trip._annotation;
            if (existingAnnotation) {
                if (tripAnnotation !== existingAnnotation.text) {
                    existingAnnotation.text = tripAnnotation;
                    this.app.sync.tripAnnotations.setItem(existingAnnotation, true);
                }
            } else {
                let newAnnotation = new TripAnnotation({
                    device: trip.device,
                    dateTime: trip._midPoint ?? trip.start,
                    text: tripAnnotation,
                    id: idSerializer.get()
                });
                trip._annotation = newAnnotation;
                this.app.sync.tripAnnotations.addItem(newAnnotation, true);
            }
            return true;
        }
        return false;
    }

    private toggleErrorMessage(id: string, showError: boolean) {
        let errorContainer = document.querySelector(`#${ id } .missing-annotation`) as HTMLElement;
        errorContainer && (showError ? show(errorContainer) : hide(errorContainer));
    }

    private save(e) {
        let id = e.currentTarget?.getAttribute("data-id") as string;
        if (id) {
            let trip = this.getTripFromId(id);
            this.toggleErrorMessage(id, false);

            let tripAnnotation = "";
            let tripCommentContainer = document.querySelector(`#${ id } .trip-comment-container`) as HTMLElement;
            if (tripCommentContainer) {
                let tripCommentField = tripCommentContainer.querySelector("input");
                if (tripCommentField && tripCommentField.value) {
                    tripAnnotation = tripCommentField.value.trim();
                    tripCommentField.value = "";
                }

                if (this.manageTripAnnotation && !tripAnnotation) {
                    this.toggleErrorMessage(id, true);
                    return;
                }
                hide(tripCommentContainer);

                if (this.addEditAnnotation(trip, tripAnnotation)) {
                    this.needsSync = true;
                }

                let tripAnnotationContainer = document.querySelector(`#${ id } .trip-annotation-container`) as HTMLElement;
                if (tripAnnotationContainer) {
                    let tripAnnotationField = tripAnnotationContainer.querySelector(".trip-annotation");
                    if (tripAnnotationField) {
                        tripAnnotationField.textContent = tripAnnotation;
                    }
                    show(tripAnnotationContainer);
                }
            }

            let selectedRadioButton = document.querySelector(`#${ id } .categorize-trip-input:checked`) as HTMLInputElement;
            if (selectedRadioButton) {
                let selectedValue = selectedRadioButton.value;
                if (selectedValue) {
                    let groupLinkedDeviceId = selectedRadioButton.getAttribute("data-grouplinkeddeviceid");
                    if (this.saveGroupLinkedDevice(id, selectedValue, groupLinkedDeviceId, trip)) {
                        this.needsSync = true;
                    }
                }
            }
        }
    }

    private saveGroupLinkedDevice(tripId: string, selectedValue: string, groupLinkedDeviceId?: string | null, trip?: Trip) {
        let currentUserTrips = this.app.store.getState().trips[(this.currentUser as IUser).id];
        let selectedTrip = currentUserTrips[tripId];
        if (!selectedTrip) {
            return false;
        }
        let groupId = "";
        let groupName = "";
        if (selectedValue === "personal") {
            groupId = "GroupPersonalGroupId";
            groupName = "Personal";
        } else if (selectedValue === "business") {
            groupId = "GroupBusinessGroupId";
            groupName = "Business";
        }

        const groupLinkedDevices = this.app.store.getState().groupLinkedDevices[selectedTrip.device.id];
        const existingGroupLinkedDevice = groupLinkedDevices && groupLinkedDeviceId ? groupLinkedDevices[groupLinkedDeviceId] as GroupLinkedDevice : undefined;

        // If the group is not changing, do nothing
        if (existingGroupLinkedDevice && groupId === existingGroupLinkedDevice.group.id) {
            return false;
        }

        if (selectedTrip.start && groupId) {
            const tripEnd = selectedTrip.stop;
            let groupLinkedDevice = new GroupLinkedDevice({
                id: groupLinkedDeviceId || idSerializer.get(),
                from: selectedTrip.start,
                to: tripEnd || "",
                group: {
                    id: groupId,
                    name: groupName
                },
                linkedEntity: {
                    id: selectedTrip.device.id
                }
            });

            if (groupLinkedDeviceId) {
                this.sync.groupLinkedDevices.setItem(groupLinkedDevice, true);
                this.addAdditionalGroupLinkedDeviceIfNecessary(groupLinkedDevice, selectedTrip, existingGroupLinkedDevice);
            } else {
                this.sync.groupLinkedDevices.addItem(groupLinkedDevice, true);
            }

            if (this.currentUser) {
                let userTrips = { ...currentUserTrips };
                let localTrip = { ...selectedTrip };
                localTrip._tripType = groupName;
                userTrips[selectedTrip.id] = localTrip;
                this.app.store.dispatch(TripActions.setTrips({ userId: this.currentUser.id, trips: userTrips, startOfDay: getTripStartOfDay(this.currentUser.timeZoneId) }));

                if (trip) {
                    trip._tripType = groupName;
                    this.recalculateDailyCounts();
                    this.displayTrips(true, tripId);
                }
            }
            return true;
        }
        return false;
    }

    private addMilliseconds (data: Date, milliseconds: number): Date {
        data.setMilliseconds(data.getMilliseconds() + milliseconds);
        return data;
    }

    private addAdditionalGroupLinkedDeviceIfNecessary(newGroupLinkedDevice: GroupLinkedDevice, selectedTrip: Trip, existingGroupLinkedDevice?: GroupLinkedDevice) {
        if (existingGroupLinkedDevice) {
            if (existingGroupLinkedDevice.from < newGroupLinkedDevice.from) {
                const adjustedFrom = this.addMilliseconds(new Date(newGroupLinkedDevice.from), -1).toISOString();
                this.sync.groupLinkedDevices.addItem(new GroupLinkedDevice({
                    id: idSerializer.get(),
                    from: existingGroupLinkedDevice.from,
                    to: adjustedFrom,
                    group: existingGroupLinkedDevice.group,
                    linkedEntity: existingGroupLinkedDevice.linkedEntity
                }), true);
            }

            if (existingGroupLinkedDevice.to > newGroupLinkedDevice.to && (!selectedTrip.nextTripStart || existingGroupLinkedDevice.to > selectedTrip.nextTripStart)) {
                const adjustedTo = this.addMilliseconds(new Date(newGroupLinkedDevice.to), 1).toISOString();
                this.sync.groupLinkedDevices.addItem(new GroupLinkedDevice({
                    id: idSerializer.get(),
                    from: adjustedTo,
                    to: existingGroupLinkedDevice.to,
                    group: existingGroupLinkedDevice.group,
                    linkedEntity: existingGroupLinkedDevice.linkedEntity
                }), true);
            }
        }
    }

    private updateTripDailySummary(tripDay: IDailyTrips, trip: Trip) {
        if (trip._tripType) {
            if (trip._tripType === "Personal") {
                tripDay.personalDistance += trip.distance || 0;
            } else if (trip._tripType === "Business") {
                tripDay.businessDistance += trip.distance || 0;
            }
        } else {
            tripDay.uncategorizedDistance += trip.distance || 0;
        }
        if (!tripDay.hasUncategorizedTrips && tripDay.uncategorizedDistance > 0) {
            tripDay.hasUncategorizedTrips = true;
        }
    }

    private recalculateDailyCounts() {
        let tripDay = this.tripsDict[this.reportDate.toISOString()];
        let updatedTripDay = {
            trips: tripDay.trips,
            hasUncategorizedTrips: false,
            businessDistance: 0,
            personalDistance: 0,
            uncategorizedDistance: 0
        };

        tripDay.trips.forEach(trip => this.updateTripDailySummary(updatedTripDay, trip));
        this.tripsDict[this.reportDate.toISOString()] = updatedTripDay;
    }

    private selectTripType(e) {
        if (!this.manageTripAnnotation) {
            this.save(e);
        } else {
            this.editAnnotation(e);
        }
    }

    private editAnnotation(e) {
        let id = e.currentTarget?.getAttribute("data-id") as string;
        if (id) {
            let tripCommentContainer = document.querySelector(`#${ id } .trip-comment-container`) as HTMLElement;
            if (isVisible(tripCommentContainer)) {
                return;
            }

            tripCommentContainer && show(tripCommentContainer);

            let tripAnnotationContainer = document.querySelector(`#${ id } .trip-annotation-container`) as HTMLElement;
            if (tripAnnotationContainer) {
                let tripAnnotationField = tripAnnotationContainer.querySelector(".trip-annotation");
                if (tripAnnotationField && tripAnnotationField.textContent && tripCommentContainer) {
                    let tripCommentField = tripCommentContainer.querySelector("input");
                    if (tripCommentField) {
                        tripCommentField.value = tripAnnotationField.textContent;
                    }
                }
                hide(tripAnnotationContainer);
            }
        }
    }

    private setTripCategorizationSyncInterval() {
        let daySpan = getLocalDaySpan(this.reportDate);
        this.app.store.dispatch(MobileActions.setTripCategorizationSyncInterval({ fromDate: daySpan.fromDate.toISOString(), toDate: daySpan.toDate.toISOString() }));
    }

    private clearTripCategorizationSyncInterval() {
        this.app.store.dispatch(MobileActions.setTripCategorizationSyncInterval({ fromDate: undefined, toDate: undefined }));
    }

    private startBackgroundSync() {
        this.clearDebounceTimeout();
        this.debounceTimer = setTimeout(() => {
            this.setTripCategorizationSyncInterval();
            let currentUser = this.app.userService.getCurrentUser();

            // Cache the current state of the page.
            // After the sync is completed, if the contents of the sync are the same as just before the sync first ran, then don't rerender.
            this.cachedTrips = this.tripsDict[getLocalDaySpan(this.reportDate).fromDate.toISOString()];
            currentUser && mainScheduler.start("@syncTrips", currentUser.name);
        }, 1500);
    }

    private renderImpl() {
        let updateTripPromise = this.updateTrips(false);
        this.startBackgroundSync();
        return updateTripPromise;
    }

    private reloadTripsOffline() {
        this.toggleRefreshSpinner(true);
        this.startBackgroundSync();
    }

    private displayTrips(loadEmpty: boolean, tripId?: string) {
        let dailyTripsObj = this.tripsDict[this.reportDate.toISOString()] || {};
        let tripsToDisplay = dailyTripsObj.trips || [];
        this.isConnected = this.app.store.getState().mobile.online === NetworkStates.ONLINE;
        let dailyView = this.template({
            hasUncategorizedTrips: !!dailyTripsObj && dailyTripsObj.hasUncategorizedTrips && this.manageGroupDevice,
            businessDistance: utils.convertToLocalDistanceString(this.app.localizerService, dailyTripsObj.businessDistance),
            personalDistance: utils.convertToLocalDistanceString(this.app.localizerService, dailyTripsObj.personalDistance),
            uncategorizedDistance: utils.convertToLocalDistanceString(this.app.localizerService, dailyTripsObj.uncategorizedDistance),
            tripsToDisplay,
            isConnected: this.isConnected,
            manageGroupDevice: this.manageGroupDevice
        });

        return this.display(dailyView, undefined)
            .then(() => {
                this.toggleLoading(true);
                let isNavigationAlreadyRendered = document.getElementById("selectDateButton");
                this.navigationView && !isNavigationAlreadyRendered && this.navigationView.setElement(document.getElementById("datepicker-container")).render();
                return this.renderUncategorizedTrips();
            })
            .then(() => {
                (tripsToDisplay.length > 0 || loadEmpty) && this.toggleLoading(false);
                // Scroll down to last categorized trip
                if (tripId) {
                    let tripContainer = document.querySelector(`.dailyTrip#${ tripId }`);
                    tripContainer && tripContainer.scrollIntoView();
                }
            })
            .catch(handleApi);
    }

    private clearDebounceTimeout() {
        this.debounceTimer && clearTimeout(this.debounceTimer);
    }
}